/* eslint-disable require-yield */
import { toast } from "react-toastify";
import { all, put, takeLatest } from "redux-saga/effects";

// Api
import {
  getPincodeRankingByTgFn,
  getRegionDataByCityId,
  getRegionNamesList,
} from "../../apis/RegionAPI";
import { getPinCodeInfo } from "../../apis/map-view/BoundariesAPI";

// Constants & Utils
import { Region } from "../../constants/action-constants/RegionActionConstants";
import { setErrorStatusCode } from "../../utils/ErrorUtils";
import { getErrorMessage } from "../../utils/util";
import { BoundarySphericalAreaFactor } from "../../constants/GeneralConstants";

// for getting region data for specific city
export function* getRegionDataByCity(action) {
  const { cityId } = action.payload;
  try {
    const regionsData = yield getRegionDataByCityId(cityId);

    const {
      aspectRatioIndexAvg,
      areaIndexAvg,
      clutterIndexAvg,
      elevationIndexAvg,
      tiltIndexAvg,
      name,
      id,
      bbox,
      center,
      population,
    } = regionsData;

    const regionDataIndexes = {
      aspectRatioIndexAvg,
      areaIndexAvg,
      clutterIndexAvg,
      elevationIndexAvg,
      tiltIndexAvg,
    };

    // required data
    const regionDataObject = {
      name,
      id,
      bbox,
      center,
      regionDataIndexes,
      population,
    };

    yield put({
      type: Region.GET_REGION_DATA_BY_CITY_SUCCESS,
      payload: { regionDataObject },
    });
  } catch (error) {
    // stores the error and render the error image when the api fails
    setErrorStatusCode(error);

    yield put({
      type: Region.GET_REGION_DATA_BY_CITY_FAILURE,
      payload: error,
    });
  }
}

export function* getRegionNames() {
  try {
    // dummy api response
    const regionsNames = yield getRegionNamesList();

    yield put({
      type: Region.GET_REGION_NAMES_SUCCESS,
      regionsNames: regionsNames,
    });
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: Region.GET_REGION_NAMES_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

// construct pincode Population Beans
function* constructPincodePopulationBeans(data) {
  const { features } = data || {};

  // if no data present
  if (!features || features.length === 0) {
    return [];
  }

  // pincode Population Beans : [{pincode: 201302, population: 23000}]
  const pincodePopulationBeans = [];

  features.forEach((feature) => {
    const { properties } = feature || {};
    const { name, area, population = {} } = properties || {};
    const { mean: meanPopulation } = population || {};

    // if area and population not present
    if (!area || !meanPopulation) {
      const bean = {
        pincode: name,
        population: 1,
      };
      pincodePopulationBeans.push(bean);
    } else {
      // area in kmSquare for complete pincode
      const boundaryArea = Math.round(area / BoundarySphericalAreaFactor);
      const bean = {
        pincode: name,
        population: meanPopulation * boundaryArea,
      };
      pincodePopulationBeans.push(bean);
    }
  });

  return pincodePopulationBeans;
}

// get Tg Attr Names String
function* getTgAttrNamesString(tgAttrMap) {
  if (!tgAttrMap || Object.keys(tgAttrMap).length === 0) {
    return "";
  }

  // tg attr names as comma separated strings
  const tgAttrNamesString = Object.values(tgAttrMap).flat().join(",");

  // remove trailing and leading spaces from each attribute name and join them with comma
  const trimmedTgAttrNamesString = tgAttrNamesString
    .split(",")
    .map((item) => item.trim())
    .join(",");

  return trimmedTgAttrNamesString;
}

export function* getPincodeRankingByTg(action) {
  const { cityId, tgAttrMap = {} } = action.payload || {};

  try {
    // get pincodes
    const { polygonData } = yield getPinCodeInfo(cityId);
    const pincodePopulationBeans = yield constructPincodePopulationBeans(
      polygonData
    );

    // get tg attr names as comma separated strings
    const tgAttrNamesString = yield getTgAttrNamesString(tgAttrMap);

    // pincode ranking
    const { items: pincodeRanking } = yield getPincodeRankingByTgFn(
      pincodePopulationBeans,
      tgAttrNamesString
    );

    yield put({
      type: Region.GET_PINCODE_RANKING_BY_TG_SUCCESS,
      payload: { pincodeRanking, polygonData, appliedTgAttrMap: tgAttrMap },
    });
  } catch (error) {
    const errorMessage = getErrorMessage(error);
    yield put({
      type: Region.GET_PINCODE_RANKING_BY_TG_FAILURE,
      payload: error,
    });
    toast.error(errorMessage);
  }
}

export default function* root() {
  yield all([
    takeLatest(Region.GET_REGION_DATA_BY_CITY, getRegionDataByCity),
    takeLatest(Region.GET_REGION_NAMES, getRegionNames),
    takeLatest(Region.GET_PINCODE_RANKING_BY_TG, getPincodeRankingByTg),
  ]);
}
